import { useEffect, useState } from "react";
import {
  LinkIcon,
  PlusIcon,
  ArrowPathIcon,
  EllipsisVerticalIcon,
  NoSymbolIcon,
  TrashIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import {
  connectConnectorApi,
  deleteConnectorApi,
  disConnectConnectorApi,
  getConnectedConnectorsApi,
  getConnectorsApi,
  getConnectorsCategoryApi,
  toggleConnectorsStatusApi,
} from "../../../services/axios/AuthApis/AssetManagementApis";
import { motion } from "framer-motion";
import ConnectorModal from "../ConnectorModal/index.js";
import { useAuth } from "../../../context/AuthContext.js";
import IntegrationModal from "../IntegrationModal/index.js";
import { FolderSync, OctagonX, Pencil, RefreshCcw } from "lucide-react";
import { useSnackbar } from "../../../context/SnackbarContext.js";
import PageLoader from "../../ui/PageLoader.js";
import ApiLoader from "../../ui/ApiLoader.js";
import Pagination from "../../ui/pagination";

export const ConnectorsTab = () => {
  const { user } = useAuth();
  const [filteredConnectors, setFilteredConnectors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConnectorModal, setShowConnectorModal] = useState(false);
  const [connectorsPagination, setConnectorsPagination] = useState({
    currentPage: 1,
    pageSize: 21,
    totalCount: 0,
    totalPages: 0,
  });
  const [openMenuConnectorId, setOpenMenuConnectorId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState({});
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [customerConnectors, setCustomerConnectors] = useState([]);
  const [integrationMode, setIntegrationMode] = useState("create");
  const [integrationData, setIntegrationData] = useState(null);
  const { showSnackbar } = useSnackbar();

  const isSuperAdmin =
    user?.role === "admin" || user?.role === "platform_admin";

  const customer_requirements = user?.customer_requirements;
  const isCustomerAdmin = user.role === "customer_admin";
  let customerId;
  if (isCustomerAdmin) {
    customerId = user?.id;
  }

  useEffect(() => {
    loadCategories();
  }, []);

  // GCP's OAuth 2.0 consent flow finishes with Google redirecting the
  // browser's full page back here (gcp_oauth_views.gcp_oauth_callback),
  // appending ?gcp_oauth=success or ?gcp_oauth=error&reason=... - there's
  // no in-app navigation event to hook into (the page reloaded), so read
  // it once on mount, show a toast, then strip it out of the URL so a
  // manual refresh doesn't re-show the toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gcpOauthResult = params.get("gcp_oauth");

    if (gcpOauthResult === "success") {
      showSnackbar("GCP connected successfully via Google OAuth", "success");
    } else if (gcpOauthResult === "error") {
      const reason = params.get("reason") || "unknown_error";
      showSnackbar(`GCP connection failed: ${reason}`, "error");
    }

    if (gcpOauthResult) {
      params.delete("gcp_oauth");
      params.delete("reason");
      const newSearch = params.toString();
      const newUrl =
        window.location.pathname +
        (newSearch ? `?${newSearch}` : "") +
        window.location.hash;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  useEffect(() => {
    loadConnectors(
      connectorsPagination.currentPage,
      connectorsPagination.pageSize,
      "",
      selectedCategory || "",
      customerId || "",
    );
  }, [
    connectorsPagination.currentPage,
    connectorsPagination.pageSize,
    selectedCategory,
    customerId,
  ]);

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setSelectedCategory(value);

    setConnectorsPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  };

  useEffect(() => {
    if (isCustomerAdmin) {
      getCustomerConnectors(customerId);
    }
  }, [customerId]);

  const getCustomerConnectors = async (customerId) => {
    try {
      const response = await getConnectedConnectorsApi(customerId);
      const connected = response.data.filter((d) => d.connected === true);
      setCustomerConnectors(connected);
    } catch (err) {
      console.error("failed to get connected connectors", err);
    }
  };

  const handleDisconnect = async (customerId, connectorId) => {
    if (!customerId) {
      showSnackbar("Customer ID not found", "error");
      return;
    }

    try {
      await disConnectConnectorApi(customerId, connectorId);
      showSnackbar("Disconnected successfully", "success");

      setCustomerConnectors((prev) =>
        prev.filter(
          (item) => String(item.connector_id) !== String(connectorId),
        ),
      );
    } catch (err) {
      showSnackbar("Disconnection failed", "error");
      console.error(err);
    }
  };

  const loadConnectors = async (
    page = 1,
    pageSize = 21,
    search = "",
    category_id = "",
    customerId = "",
  ) => {
    try {
      setLoading(true);
      console.log("Loading connectors...");

      const data = await getConnectorsApi(
        page,
        pageSize,
        search,
        category_id,
        customerId,
      );
      console.log("Connectors loaded:", data);

      let finalData = data.results || [];
      setConnectors(finalData);
      setConnectorsPagination({
        currentPage: data.current_page,
        pageSize: data.page_size,
        totalCount: data.count,
        totalPages: data.total_pages,
      });
      // if (isSuperAdmin) {
      //   setConnectors(finalData);

      //   // 📄 Set pagination
      //   setConnectorsPagination({
      //     currentPage: data.current_page,
      //     pageSize: data.page_size,
      //     totalCount: data.count,
      //     totalPages: data.total_pages,
      //   });
      // } else {
      //   // console.log({ customer_requirements: customer_requirements.AIAssestManager })
      //   const customerConnectors = customer_requirements?.AIAssestManager;
      //   setConnectors(customerConnectors);
      //   setConnectorsPagination({
      //     currentPage: page,
      //     pageSize: pageSize,
      //     totalCount: customerConnectors.length,
      //     totalPages: Math.ceil(customerConnectors.length / pageSize),
      //   });
      // }
    } catch (error) {
      console.error("Failed to load connectors:", error);

      setConnectors([]);
      setConnectorsPagination({
        currentPage: 1,
        pageSize: 20,
        totalCount: 0,
        totalPages: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConnectorsPageChange = (newPage, newPageSize) => {
    const pageSize = newPageSize !== undefined ? newPageSize : connectorsPagination.pageSize;
    setLoading(true);
    loadConnectors(
      newPage,
      pageSize,
      "",
      selectedCategory || "",
      customerId || "",
    );
  };

  const loadCategories = async () => {
    try {
      const categories = await getConnectorsCategoryApi();
      setCategories(categories.results);
    } catch (error) {
      console.error("Failed to load connectors:", error);
      setConnectors([]);
    }
  };

  const handleStatusToggle = async (id) => {
    try {
      const response = await toggleConnectorsStatusApi(id);
      setConnectors((prev) =>
        prev.map((c) => (c.id === id ? { ...c, Active: response.active } : c)),
      );
      if (response.status === "success") {
        showSnackbar(
          `Connector ${
            response.active === true ? "Activated" : "Deactivated"
          } Successfully`,
          "success",
        );
      }
      console.log({ response: response });
    } catch (error) {
      showSnackbar("Failed to update status for connector", "error");
    }
  };

  const refreshList = (action, connector) => {
    if (action === "create") {
      setConnectors((prev) => [connector, ...prev]);
    }

    if (action === "edit") {
      setConnectors((prev) =>
        prev.map((c) => (c.id === connector.id ? connector : c)),
      );
    }

    if (action === "delete") {
      setConnectors((prev) => prev.filter((c) => c.id !== connector.id));
    }
  };

  const handleDeleteConnector = async (id) => {
    try {
      await deleteConnectorApi(id);
      refreshList("delete", { id });
      showSnackbar("Connector deleted", "success");
    } catch (err) {
      showSnackbar("Failed to delete", "error");
    }
  };

  const handleSync = async (connectorId) => {
    setLoading(true);
    try {
      // The server syncs with the credentials it already holds; the browser
      // never has the secret.
      await connectConnectorApi({
        customer_id: customerId,
        connector_id: connectorId,
      });

      showSnackbar("Connector synced successfully", "success");
    } catch (error) {
      console.error("Error creating connection:", error);
      showSnackbar("Failed to connect", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h2 className="text-lg font-medium text-gray-900">
            Asset Connectors
          </h2>
          <div className="flex flex-col md:flex-row justify-between gap-4 items-center">
            {/* Category filter */}
            <div className="flex items-center gap-2">
              <label
                htmlFor="category"
                className="text-sm font-medium text-gray-700"
              >
                Category:
              </label>
              <select
                id="category"
                value={selectedCategory}
                onChange={handleCategoryChange}
                className="block w-full md:w-48 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:outline-none"
              >
                <option value="">All</option>
                {/* Dynamically populate categories */}
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Add Connector Button */}

            {isSuperAdmin ? (
              <button
                onClick={() => {
                  setIsEditMode(false);
                  setSelectedConnector({});
                  setShowConnectorModal(true);
                }}
                className="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-blue-700"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Connector
              </button>
            ) : (
              ""
            )}
          </div>
        </div>

        {loading && connectors.length > 0 && <ApiLoader variant="bar" />}
        {loading && connectors.length === 0 ? (
          <ApiLoader label="Loading Connectors..." className="py-20" />
        ) : connectors.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-gray-600">No connectors found</div>
          </div>
        ) : (
          <div aria-busy={loading} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-opacity ${loading ? 'pointer-events-none opacity-50' : ''}`}>
            {connectors.map((connector) => {
              const connectorId = connector.id;
              const isMenuOpen = openMenuConnectorId === connectorId;
              const isConnected = customerConnectors.some(
                (item) => String(item.connector_id) === String(connector.id),
              );
              const connectedData = customerConnectors.find(
                (item) => String(item.connector_id) === String(connector.id),
              );

              return (
                <motion.div
                  key={connector.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 p-6"
                >
                  {/* Top Section */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="space-y-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {connector.name}
                      </h3>
                    </div>

                    {/* Toggle */}
                    {/* {isSuperAdmin ? (
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={!!connector.Active}
                          onChange={() => handleStatusToggle(connector.id)}
                        />
                        <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-blue-600 transition-all"></div>
                        <span className="absolute left-[3px] top-[3px] h-[18px] w-[18px] bg-white rounded-full shadow transform transition-all duration-300 peer-checked:translate-x-5"></span>
                      </label>
                    ) : (
                      ""
                    )} */}

                    {isSuperAdmin ? (
                      <div className=" ">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuConnectorId(
                              isMenuOpen ? null : connectorId,
                            );
                          }}
                          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <EllipsisVerticalIcon className="w-5 h-5 text-black" />
                        </button>

                        {/* Dropdown menu */}
                        {isMenuOpen && (
                          <div className="absolute mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowConnectorModal(true);
                                setIsEditMode(true);
                                setSelectedConnector(connector);
                                setOpenMenuConnectorId(null);
                              }}
                              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              <PencilIcon className="w-4 h-4 mr-3 text-orange-600" />
                              Edit
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteConnector(connector.id);
                              }}
                              className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <TrashIcon className="w-4 h-4 mr-3" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-2"
                      >
                        {isConnected ? (
                          <div className="flex items-center gap-2">
                            {/* Sync */}
                            <button
                              onClick={() => handleSync(connector.id)}
                              className="inline-flex w-10 h-10 items-center px-3 py-2 rounded-lg bg-green-200 hover:bg-green-300"
                              title="Sync"
                            >
                              <RefreshCcw className="text-green-600" />
                            </button>

                            {/* Edit */}
                            <button
                              title="Edit"
                              onClick={() => {
                                setIntegrationMode("edit");
                                setSelectedConnector(connector);
                                setIntegrationData(connectedData);
                                setShowIntegrationModal(true);
                              }}
                              className="inline-flex items-center w-10 h-10 px-3 py-2 rounded-lg bg-orange-100 text-orange-200 hover:bg-orange-300"
                            >
                              <Pencil className=" text-orange-600" />
                            </button>

                            {/* Disconnect */}
                            <button
                              title="Disconnect"
                              onClick={() =>
                                handleDisconnect(customerId, connector.id)
                              }
                              className="inline-flex items-center w-10 h-10 px-3 py-2 rounded-lg bg-red-200 text-white hover:bg-red-300"
                            >
                              <OctagonX className="w-full h-full text-red-600" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setIntegrationMode("create");
                              setIntegrationData(null);
                              setSelectedConnector(connector);
                              setShowIntegrationModal(true);
                            }}
                            className="inline-flex items-center px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                          >
                            Connect
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-gray-100 mb-4"></div>

                  {/* Middle Section */}
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 font-medium">Type:</span>
                      <span className="text-gray-800">
                        {connector.connector_type}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 font-medium">Status:</span>
                      {isSuperAdmin ? (
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={!!connector.Active}
                            onChange={() => handleStatusToggle(connector.id)}
                          />
                          <div className="w-11 h-6 bg-gray-500 rounded-full peer peer-checked:bg-green-600 transition-all"></div>
                          <span className="absolute left-[3px] top-[3px] h-[18px] w-[18px] bg-white rounded-full shadow transform transition-all duration-300 peer-checked:translate-x-5"></span>
                        </label>
                      ) : (
                        <span
                          className={`font-medium ${
                            connector.Active
                              ? "text-green-600"
                              : "text-gray-500"
                          }`}
                        >
                          {connector.Active ? "Active" : "Inactive"}
                        </span>
                      )}
                      {/*  */}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
        {connectorsPagination.totalCount > 0 && (
          <Pagination
            currentPage={connectorsPagination.currentPage}
            totalPages={connectorsPagination.totalPages}
            totalCount={connectorsPagination.totalCount}
            pageSize={connectorsPagination.pageSize}
            onPageChange={(newPage, newPageSize) => handleConnectorsPageChange(newPage, newPageSize)}
            loading={loading}
          />
        )}
      </div>
      {showConnectorModal && (
        <ConnectorModal
          open={showConnectorModal}
          onClose={() => setShowConnectorModal(false)}
          categories={categories}
          refreshList={refreshList}
          isEditMode={isEditMode}
          selectedConnector={selectedConnector}
        />
      )}
      {showIntegrationModal && (
        <IntegrationModal
          open={showIntegrationModal}
          onClose={() => setShowIntegrationModal(false)}
          selectedConnector={selectedConnector}
          customerConnectors={customerConnectors}
          setCustomerConnectors={setCustomerConnectors}
          integrationMode={integrationMode}
          integrationData={integrationData}
        />
      )}
    </>
  );
};
